import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save, BookOpen } from 'lucide-react';
import { ContentCategory } from '@brovicms/models';
import { quoteService, QuoteFormData } from '../../services/quoteService';
import ebookService from '../../services/ebookService';
import toast from 'react-hot-toast';
import { CONTENT_CATEGORIES } from '../../constants/categories';

interface AddQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddQuoteModal: React.FC<AddQuoteModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedBook, setSelectedBook] = useState<{ id: string; title: string } | null>(null);
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [books, setBooks] = useState<{ id: string; title: string }[]>([]);
  const [bookSearchTerm, setBookSearchTerm] = useState('');
  const [filteredBooks, setFilteredBooks] = useState<{ id: string; title: string }[]>([]);
  const [publishImmediately, setPublishImmediately] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch
  } = useForm<QuoteFormData>({
    defaultValues: {
      content: '',
      source: '',
      category: {
        id: '',
        name: '',
        slug: '',
        isActive: true,
        sortOrder: 0
      },
      tags: [],
      language: 'en',
      isPublished: false,
      isFeatured: false,
      isPremium: false
    }
  });

  useEffect(() => {
    if (isOpen) {
      // Load categories from constants
      setCategories(CONTENT_CATEGORIES);

      // Load e-books from database
      const loadBooks = async () => {
        try {
          const result = await ebookService.getEBooks({ limit: 50 });
          if (result.data) {
            const bookList = result.data.map(book => ({
              id: book.id,
              title: book.title
            }));
            setBooks(bookList);
          }
        } catch (error) {
          console.error('Error loading books:', error);
          // Fallback to mock data if service fails
          setBooks([
            { id: '1', title: 'The Power of Now' },
            { id: '2', title: 'Atomic Habits' },
            { id: '3', title: 'Think and Grow Rich' },
            { id: '4', title: 'The 7 Habits of Highly Effective People' }
          ]);
        }
      };

      loadBooks();
    }
  }, [isOpen]);

  // Filter books based on search term
  useEffect(() => {
    if (bookSearchTerm.trim() === '') {
      setFilteredBooks(books);
    } else {
      const filtered = books.filter(book =>
        book.title.toLowerCase().includes(bookSearchTerm.toLowerCase())
      );
      setFilteredBooks(filtered);
    }
  }, [books, bookSearchTerm]);

  const onSubmit = async (data: QuoteFormData) => {
    setIsSubmitting(true);
    try {
      // Find the selected category from the categories list
      const selectedCategory = categories.find(cat => cat.id === data.category.id);
      
      // Create the complete quote data with proper category object
      const quoteData: QuoteFormData = {
        ...data,
        isPublished: publishImmediately,
        category: selectedCategory || {
          id: data.category.id,
          name: categories.find(cat => cat.id === data.category.id)?.name || 'Unknown',
          slug: categories.find(cat => cat.id === data.category.id)?.slug || 'unknown',
          isActive: true,
          sortOrder: 0
        }
      };

      const result = await quoteService.createQuote(quoteData);
      if (result.success) {
        toast.success('Quote created successfully');
        reset();
        setSelectedBook(null);
        onSuccess();
        onClose();
      } else {
        toast.error(result.error || 'Failed to create quote');
      }
    } catch (error: any) {
      console.error('Create quote error:', error);
      toast.error('An error occurred while creating the quote');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBookSelect = (book: { id: string; title: string }) => {
    setSelectedBook(book);
    setValue('bookReference', {
      bookId: book.id,
      bookTitle: book.title,
      pageNumber: undefined,
      chapter: undefined
    });
  };

  const removeBookReference = () => {
    setSelectedBook(null);
    setValue('bookReference', undefined);
  };

  const handleClose = () => {
    reset();
    setSelectedBook(null);
    setBookSearchTerm('');
    setPublishImmediately(true);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Add New Quote</h2>
              <p className="text-primary-100 mt-1">Create an inspirational quote for your collection</p>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-primary-200 transition-colors p-2 hover:bg-white hover:bg-opacity-20 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="p-8 flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            {/* Quote Details */}
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quote Details</h3>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quote Text <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      {...register('content', { required: 'Quote content is required' })}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                      placeholder="Enter the inspirational quote text..."
                    />
                    {errors.content && (
                      <p className="text-red-500 text-sm mt-2 flex items-center">
                        <span className="w-4 h-4 mr-1">⚠️</span>
                        {errors.content.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Author/Source
                    </label>
                    <input
                      {...register('source')}
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                      placeholder="Who said this quote?"
                    />
                  </div>
                </div>
              </div>

              {/* Book Reference */}
              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
                  Book Reference
                </h3>
                
                {selectedBook ? (
                  <div className="bg-white rounded-lg p-4 border-2 border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <BookOpen className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{selectedBook.title}</p>
                          <p className="text-sm text-gray-500">Linked to book</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={removeBookReference}
                        className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Link to Book (Optional)
                    </label>
                    
                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={bookSearchTerm}
                        onChange={(e) => setBookSearchTerm(e.target.value)}
                        className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="Search for a book..."
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    </div>

                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                      {filteredBooks.length > 0 ? (
                        <div className="divide-y divide-gray-200">
                          {filteredBooks.map((book) => (
                            <button
                              key={book.id}
                              type="button"
                              onClick={() => handleBookSelect(book)}
                              className="w-full text-left p-4 hover:bg-blue-50 transition-colors flex items-center"
                            >
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                <BookOpen className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{book.title}</p>
                                <p className="text-sm text-gray-500">Click to link this book</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 text-center text-gray-500">
                          <BookOpen className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p>{bookSearchTerm ? 'No books found matching your search' : 'No books available'}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {selectedBook && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Page Number
                      </label>
                      <input
                        {...register('bookReference.pageNumber')}
                        type="number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Chapter
                      </label>
                      <input
                        {...register('bookReference.chapter')}
                        type="text"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Category */}
              <div className="bg-green-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Category</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={watch('category')?.id || ""}
                    onChange={(e) => {
                      const selectedCategory = CONTENT_CATEGORIES.find(cat => cat.id === e.target.value);
                      if (selectedCategory) {
                        setValue('category', {
                          id: selectedCategory.id,
                          name: selectedCategory.name,
                          slug: selectedCategory.slug,
                          isActive: selectedCategory.isActive,
                          sortOrder: selectedCategory.sortOrder,
                        });
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="text-red-500 text-sm mt-2 flex items-center">
                      <span className="w-4 h-4 mr-1">⚠️</span>
                      {errors.category.message}
                    </p>
                  )}
                </div>
              </div>

              {/* Publish Options */}
              <div className="bg-purple-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Publish Options</h3>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="publishImmediately"
                    checked={publishImmediately}
                    onChange={(e) => setPublishImmediately(e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="publishImmediately" className="ml-3 block text-sm font-medium text-gray-700">
                    Publish immediately
                  </label>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {publishImmediately 
                    ? "Quote will be published and visible to users immediately" 
                    : "Quote will be saved as draft and can be published later"
                  }
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Quote
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddQuoteModal;
